import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";

/**
 * AnonymousAnalyzeGate — Slice 1 (2026-02-14).
 *
 * Rendered by /analyze/{ticker} when the backend returns 401 and the
 * frontend has no evidence the visitor was ever authenticated on this
 * browser.  Replaces the previous "Session expired" surface for
 * anonymous visitors with conversion-friendly marketing copy.
 *
 * Behavior notes:
 *   * The ticker travels through both CTA URLs as a validated
 *     internal `?next=/analyze/{TICKER}` so Login / Signup /
 *     Google-callback all return the visitor to the same analysis
 *     they originally requested.
 *   * The primary CTA is visually dominant (Button default) and the
 *     "Log in" affordance is intentionally secondary (ghost/outline).
 *   * Every string comes from the `analyze.anonymous_gate` i18n
 *     namespace so English and German render at exact parity from a
 *     single semantic slot.
 *   * NO toast is fired from this surface; the caller
 *     (`Analyze.js`) explicitly refrains from a toast when the
 *     visitor was never authenticated.
 */
export default function AnonymousAnalyzeGate({ ticker, testId = "analyze-anonymous-gate" }) {
  const { t } = useTranslation();
  const tickerUpper = (ticker || "").toUpperCase();
  const nextPath = tickerUpper ? `/analyze/${tickerUpper}` : "/analyze";
  const encoded = encodeURIComponent(nextPath);
  const signupHref = `/signup?next=${encoded}`;
  const loginHref = `/login?next=${encoded}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid={testId}
      data-anonymous-gate-ticker={tickerUpper || null}
      className="mx-auto mt-8 sm:mt-12 max-w-2xl"
    >
      <div className="rounded-2xl border border-border/70 bg-card/60 p-6 sm:p-10 shadow-[var(--app-shadow-elev-1)] backdrop-blur">
        <div className="flex flex-col items-center text-center">
          <div
            className="inline-flex w-14 h-14 sm:w-16 sm:h-16 items-center justify-center rounded-2xl mb-4"
            style={{
              background: "rgba(63,174,137,0.10)",
              border: "1px solid rgba(63,174,137,0.30)",
              color: "#3FAE89",
            }}
            aria-hidden
          >
            <Sparkles className="w-7 h-7 sm:w-8 sm:h-8" />
          </div>
          <h1
            className="font-display text-2xl sm:text-3xl tracking-tight"
            data-testid={`${testId}-title`}
          >
            {t("analyze.anonymous_gate.title", { ticker: tickerUpper })}
          </h1>
          <p
            className="mt-3 text-sm sm:text-[15px] text-muted-foreground leading-relaxed max-w-md"
            data-testid={`${testId}-body`}
          >
            {t("analyze.anonymous_gate.body")}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto">
            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto px-6 shadow-[var(--app-shadow-elev-1)]"
              data-testid={`${testId}-primary-cta`}
            >
              <Link to={signupHref}>
                {t("analyze.anonymous_gate.primary_cta")}
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="lg"
              className="w-full sm:w-auto"
              data-testid={`${testId}-secondary-cta`}
            >
              <Link to={loginHref}>
                {t("analyze.anonymous_gate.secondary_cta")}
              </Link>
            </Button>
          </div>
          <p
            className="mt-5 text-[12px] text-muted-foreground/80"
            data-testid={`${testId}-microcopy`}
          >
            {t("analyze.anonymous_gate.microcopy")}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
